import React, { useState, useEffect } from "react";
import { Typography, Button, IconButton, Snackbar } from "@mui/material";
import "../assets/gamefootage.css";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  playersById,
  gameFootage,
  gameFootage2,
  getAllGameFootageSortedByTime,
} from "../assets/testgame-players";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

const GameFootage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const player_id = searchParams.get("player_id");
  const [player, setPlayer] = useState(null);

  useEffect(() => {
    // Fetch player list
    axios
      .get(`/api/players/${player_id}`)
      .then((response) => {
        setPlayer(response.data);
      })
      .catch(() => {
        toast.error("Failed to load players");
      });
  }, []);

  // Map URL parameter types to gameFootage categories
  const typeToCategory = {
    fgm: "field_goals",
    three_pm: "threes",
    ftm: "free_throws",
    rebounds: "rebounds",
    assists: "assists",
    steals: "steals",
    turnovers: "turnovers",
  };

  const urlType = searchParams.get("type");
  const initialFilter = urlType ? typeToCategory[urlType] || "all" : "all";
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const playerData = playersById[player_id] || {};

  // Create a flat array of all clips with their categories and sort by timestamp
  const sortedClips = Object.entries(gameFootage2)
    .flatMap(([category, clips]) =>
      clips.map((clip, index) => ({
        ...clip,
        category,
        clipIndex: index + 1,
      }))
    )
    .sort((a, b) => {
      // Convert timestamps to comparable values (assuming format "HH:MM:SS")
      const timeToSeconds = (timestamp) => {
        const [hours, minutes, seconds] = timestamp.split(":").map(Number);
        return hours * 3600 + minutes * 60 + seconds;
      };
      return timeToSeconds(a.timestamp) - timeToSeconds(b.timestamp);
    });

  const filteredClips =
    selectedFilter === "all"
      ? sortedClips
      : sortedClips.filter((clip) => clip.category === selectedFilter);

  const [isPlaying, setIsPlaying] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [currentClip, setCurrentClip] = useState(filteredClips[0] || null);

  const handlePlayClick = () => {
    setIsPlaying(!isPlaying);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <div className="game-footage-container">
      <div className="game-footage-top-bar">
        <div className="game-footage-logo-container">
          <img src="/xsmlgsm.PNG" alt="Logo" className="game-footage-logo" />
          <Typography className="game-footage-logo-text">
            Player Footage
          </Typography>
        </div>
        <IconButton
          className="game-footage-close-button"
          onClick={() => navigate(-1)}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className="game-footage-layout">
        {/* Video Player Section */}
        <div className="game-footage-player-section">
          <div className="game-footage-video-player">
            <img
              src="https://peterhuoppi.com/wp-content/uploads/2014/03/scoreboard.jpg"
              alt="Video player"
              className="game-footage-video-placeholder"
            />
            <div className="game-footage-video-controls">
              <div className="game-footage-progress-bar">
                <div
                  className="game-footage-progress"
                  style={{ width: "30%" }}
                ></div>
              </div>
              <div className="game-footage-controls-buttons">
                <div className="game-footage-controls-left">
                  <IconButton
                    className="game-footage-control-button"
                    onClick={handlePlayClick}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                  <div className="game-footage-time-display">
                    <span>0:42</span>
                    <span className="game-footage-time-separator">/</span>
                    <span>2:15</span>
                  </div>
                </div>
                <div className="game-footage-controls-right">
                  <IconButton className="game-footage-control-button">
                    <VolumeUpIcon />
                  </IconButton>
                  <IconButton className="game-footage-control-button">
                    <FullscreenIcon />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
          <div className="game-footage-video-info-bar">
            <Typography variant="h6" className="game-footage-current-title">
              {player?.player_name} - {currentClip?.action || "Action"} -{" "}
              {currentClip?.game || "Game"}
            </Typography>
            <Typography variant="h7" style={{ color: "gray" }}>
              {currentClip?.lead_up_actions
                ? currentClip.lead_up_actions.join(" > ") +
                  " > " +
                  currentClip.action
                : "No lead-up actions available"}
            </Typography>
          </div>
        </div>

        {/* Playlist Section */}
        <div className="game-footage-playlist-section">
          <div className="game-footage-playlist-header">
            <Typography variant="h6">Clips ({filteredClips.length})</Typography>
          </div>
          <div className="game-footage-filter-bar">
            <Button
              variant={selectedFilter === "all" ? "contained" : "outlined"}
              onClick={() => setSelectedFilter("all")}
              className="game-footage-filter-button"
            >
              All
            </Button>
            {Object.keys(gameFootage2).map((category) => (
              <Button
                key={category}
                variant={selectedFilter === category ? "contained" : "outlined"}
                onClick={() => setSelectedFilter(category)}
                className="game-footage-filter-button"
              >
                {category.replace("_", " ").toUpperCase()}
              </Button>
            ))}
          </div>
          <div className="game-footage-video-list">
            {filteredClips.map((clip, index) => (
              <div
                key={index}
                className="game-footage-video-item"
                onClick={() => setCurrentClip(clip)}
              >
                <div className="game-footage-video-number">#{index + 1}</div>
                <div className="game-footage-video-info">
                  <Typography className="game-footage-video-title">
                    {clip.category.toUpperCase()}
                  </Typography>
                  <Typography className="game-footage-video-timestamp">
                    {clip.duration}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="This is a Demo - Game Footage Coming Soon"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </div>
  );
};

export default GameFootage;
